<template>
	<div class="container">
		<div class="row mb-4">
			<div class="col-12 d-flex align-items-center">
				<h1 class="mb-0">{{ $t('upload-form-responses') }}</h1>
			</div>
		</div>

		<StepIndicator v-if="step <= 4" :steps="stepLabels" :current-step="step" class="mb-4" />

		<StepSelectForm
			v-if="step === 1"
			:forms="forms"
			:form-id.sync="formId"
			:status.sync="status"
			:signoff-users="signoffUsers"
			:signoff-user-id.sync="signoffUserId"
			@next="onFormChosen"
		/>

		<StepPreview
			v-if="step === 2"
			:title="$t('upload-form-responses-csv')"
			:loading-preview="loadingPreview"
			:preview="preview"
			@file-selected="onFileSelected"
			@next="step = 3"
			@back="step = 1"
		/>

		<StepMapColumns
			v-if="step === 3"
			:instructions="$t('match-form-fields-to-csv-columns')"
			:field-defs="fieldDefs"
			:column-options="columnOptions"
			:column-map="columnMap"
			:validating="validating"
			:mapping-valid="mappingValid"
			:preview-rows="preview ? preview.rows : []"
			:loading-fields="loadingFields"
			@validate="runValidation"
			@back="step = 2"
		/>

		<StepReviewIssues
			v-if="step === 4"
			:validation="validation"
			:error-count="errorCount"
			:warning-count="warningCount"
			:skip-invalid-rows.sync="skipInvalidRows"
			:importing="importing"
			@import="runImport"
			@back="step = 3"
		/>

		<StepResults
			v-if="step === 5"
			:import-results="importResults"
			@reset="reset"
		/>
	</div>
</template>

<script>
import { CsvUploadWizardMixin } from '@/mixins';
import StepIndicator    from 'src/components/Util/StepIndicator.vue';
import StepSelectForm   from 'src/components/Forms/Upload/StepSelectForm.vue';
import StepPreview      from 'src/components/Util/Upload/StepPreview.vue';
import StepMapColumns   from 'src/components/Util/Upload/StepMapColumns.vue';
import StepReviewIssues from 'src/components/Forms/Upload/StepReviewIssues.vue';
import StepResults      from 'src/components/Forms/Upload/StepResults.vue';

export default {
	mixins: [CsvUploadWizardMixin],

	components: {
		StepIndicator,
		StepSelectForm,
		StepPreview,
		StepMapColumns,
		StepReviewIssues,
		StepResults,
	},

	data() {
		return {
			step:       1,
			stepLabels: ['Form & Status', 'Preview', 'Map Columns', 'Review Issues'],

			// Step 1
			forms:         [],
			formId:        null,
			status:        'draft',
			signoffUsers:  [],
			signoffUserId: null,

			// Step 3
			loadingFields: false,
			fieldDefs:     [],
			columnMap:     {},
		};
	},

	computed: {
		csvEndpoints() {
			return {
				preview:  '/forms/response/upload/preview',
				validate: '/forms/response/upload/validate',
				import:   '/forms/response/upload/import',
			};
		},
	},

	mounted() {
		this.getForms();
		this.getSignoffUsers();
	},

	methods: {
		extraFormData() {
			return { form_id: this.formId };
		},

		getForms() {
			this.$cfetch(`${this.$settings.APIBaseURL}/forms/response/upload/selectform`)
				.then(json => {
					if (json.status === 'SUCCESS') this.forms = json.forms;
				})
				.catch(err => this.$log.error.call(this, err));
		},

		getSignoffUsers() {
			this.$cfetch(`${this.$settings.APIBaseURL}/forms/response/upload/selectstaff`)
				.then(json => {
					if (json.status === 'SUCCESS') this.signoffUsers = json.users;
				})
				.catch(err => this.$log.error.call(this, err));
		},

		onFormChosen() {
			this.step = 2;
			this.fetchFields();
		},

		fetchFields() {
			this.loadingFields = true;
			this.fieldDefs = [];
			this.columnMap = {};

			const fd = this.buildFormData();

			fetch(`${this.$settings.APIBaseURL}/forms/response/upload/fields`, { method: 'POST', body: fd })
				.then(r => r.json())
				.then(res => {
					if (res.status === 'SUCCESS') {
						this.fieldDefs = res.fields;
						this.columnMap = Object.fromEntries(res.fields.map(f => [f.key, null]));
						if (this.preview) this.autoDetectColumns(this.preview.headers);
					}
				})
				.catch(err => console.error('Fields error:', err))
				.finally(() => { this.loadingFields = false; });
		},

		onFileSelected(file) {
			this.fetchPreview(file);
		},

		runValidation() {
			this.postValidation().then(res => {
				if (res && res.status === 'SUCCESS') this.step = 4;
			});
		},

		runImport() {
			this.postImport({
				status: this.status,
				usersignofffrom_id: this.status === 'signedoff' ? this.signoffUserId : '',
			}).then(res => {
				if (res && res.status === 'SUCCESS') this.step = 5;
			});
		},

		// Overrides CsvUploadWizardMixin's reset(): that helper wipes $data back to
		// data()'s defaults (forms: []), but forms/signoffUsers are fetched once in
		// mounted() rather than re-fetched per step, so re-fetch them here too.
		reset() {
			Object.assign(this.$data, this.$options.data.call(this));
			this.getForms();
			this.getSignoffUsers();
		},
	},
};
</script>
